import "./views.scss";

import { useEffect, useState } from "react";

import PrimaryButton from "../components/buttons/PrimaryButton";
import DynamicFormField from "../components/forms/formFields/DynamicFormField";
import { useFormFlowContext } from "../components/forms/FormFlowContext";
import LoadingBeacon from "../components/loading/LoadingBeacon";
import { triggerCommunityRegistrationView } from "../helpers/observability/analytics";
import { findAccountWithoutHandshake } from "../services/requests";
import appSettings from "../settings/appSettings";

/**
 * WelcomePage component renders a welcome page with form fields to capture user information and navigation buttons.
 *
 * @param {Object} props - The component props.
 * @param {Object} props.formMethods - Methods for handling form state and actions.
 *
 * @returns {JSX.Element} The rendered WelcomePage component.
 */
const WelcomePage = ({ formMethods }) => {
  const [findingAccount, setFindingAccount] = useState(false);
  const { pageForward, setCurrentPage, setView, noHandshake, setUserData, userData } =
    useFormFlowContext();

  const { getFormState } = formMethods;
  const formState = getFormState();

  const { welcomeConfig, widgetId, wvusLogger } = appSettings;
  const {
    headerText,
    personalizeHeader,
    subheaderText,
    formInstructions,
    fields: configFields = []
  } = welcomeConfig;

  const requiredFieldsNames = configFields
    .filter((field) => field.required)
    .map((field) => field.name);
  const validToClickNext = requiredFieldsNames.every((name) => formState?.fields[name]?.value);
  const displayHeaderFromConfig =
    personalizeHeader && userData?.firstName ? `${headerText}, ${userData.firstName}!` : headerText;

  useEffect(() => {
    triggerCommunityRegistrationView({
      event_action: "user_info",
      event_label: "required_info"
    });
  }, []);

  const onFindAccountError = (err, userDataFromForm) => {
    const errorMessage = err?.message || "Error finding account without handshake";
    const statusCode = err?.resp?.status || "error";
    wvusLogger.log({ errorMessage, statusCode }, wvusLogger.getLogLevelNames().ERROR);
    setUserData(userDataFromForm);
    setView("community_hub_interest");
  };

  // only happens in the case of noHandshake (with no userData already set)
  const onFindAccountSuccess = (response, userDataFromForm) => {
    if (
      response?.data?.hasBettermodeAcct === undefined ||
      response?.data?.hasAuth0Acct === undefined
    ) {
      return;
    }
    const responseData = response.data || {};
    const { hasAuth0Acct, hasBettermodeAcct } = responseData;

    setUserData({ ...userDataFromForm, ...responseData });

    if (hasAuth0Acct && hasBettermodeAcct) {
      setView("signin");
    } else {
      pageForward();
    }  
  };

  const inputMatchesUserData = (fields, userData) => {
    if (!userData) {
      return false;
    } else {
      return ["email", "firstName", "lastName"].every((field) => fields[field]?.value === userData[field]);
    }
  }

  const clickNext = () => {
    if (noHandshake) {
      const { email, firstName, lastName } = formState?.fields || {};
      if (!email?.value || !firstName?.value || !lastName?.value || !widgetId) {
        wvusLogger.log(
          {
            error:
              "Need email, firstName, lastName, and registration type to find account without handshake."
          },
          wvusLogger.getLogLevelNames().ERROR
        );
        return;
      }

      if (inputMatchesUserData(formState?.fields, userData)) {
        // Has the same inputted data. 
        if (userData?.hasAuth0Acct && userData?.hasBettermodeAcct) {
          // User has both accounts, so go to the signin page.
          setView("signin");
        } else  {
          // User has only a Bettermode account, so go to the community hub interest page.
          pageForward();
        }
        return;
      }

      setFindingAccount(true);

      const formData = {
        email: email.value,
        lastName: lastName.value,
        firstName: firstName.value
      };

      findAccountWithoutHandshake({ ...formData, regtype: widgetId })
        .then((resp) => onFindAccountSuccess(resp, formData))
        .catch((err) => onFindAccountError(err, formData))
        .finally(() => setFindingAccount(false));
      return;
    } else {
      Object.entries(formState?.fields).forEach(([name, answer]) => {
        if (!answer.isValid && answer.optional) {
          formMethods.setValueChange(name, "");
        }
      });
      pageForward();
    }
  };

  const getFieldData = (fieldConfigData) => {
    if (fieldConfigData.id === "email") {
      return {
        ...fieldConfigData,
        editable: noHandshake === true
      };
    }

    return { ...fieldConfigData };
  }

  if (findingAccount) {
    return (
      <div className="views display-flex flex-col align-center">
        <h1 className="serif">
          <strong>Finding your account...</strong>
        </h1>
        <LoadingBeacon />
      </div>
    );
  }

  return (
    <div className="views display-grid gap-tight">
      <div className="header">
        <h1 className="serif">
          <strong>{headerText ? displayHeaderFromConfig : "Welcome, Friend!"}</strong>
        </h1>
      </div>
      {subheaderText && <p className="base mb-0">{subheaderText}</p>}
      {formInstructions && <p className="base form_bold">{formInstructions}</p>}

      <div className="form-container-grid">
        {configFields.map((field) => (
          <DynamicFormField
            key={field.id}
            formMethods={formMethods}
            type={field.type}
            validationType={field.validationType}
            field={getFieldData(field)}
            formFields={formState?.fields}
          />
        ))}
      </div>

      <footer className="footer">
        <PrimaryButton handleClick={() => clickNext()} disabled={!validToClickNext}>
          Next
        </PrimaryButton>
        {!noHandshake && (
          <p
            className="btn-text-blue base navigate"
            onClick={() => {
              setView("wrong_info");
              setCurrentPage(null);
            }}>
            <strong>Not you? We can help.</strong>
          </p>
        )}
      </footer>
    </div>
  );
};

export default WelcomePage;
